import { Injectable, signal, computed, effect } from '@angular/core';
import { StorageService } from './storage/storage.service';
import { PlatformService } from './platform.service';
import { UserLocalization } from '@shared/models/locale/locale.models';

// Import translations directly - this is synchronous and works immediately (i18n is under src, not in publicDir)
import enTranslations from '../../../i18n/en_US.json';
import ukTranslations from '../../../i18n/uk_UA.json';
import ruTranslations from '../../../i18n/ru_UA.json';

type TranslationObject = Record<string, any>;

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  // Load translations synchronously from imports
  private readonly translations: Record<string, TranslationObject> = {
    en_US: enTranslations as TranslationObject,
    uk_UA: ukTranslations as TranslationObject,
    ru_UA: ruTranslations as TranslationObject,
  };

  private readonly currentLocale = signal<UserLocalization>(UserLocalization.uk_UA);

  public readonly currentLocaleSignal = computed(() => this.currentLocale());

  constructor(
    private storageService: StorageService,
    private platformService: PlatformService,
  ) {
    // Initialize locale from storage
    if (this.platformService.isBrowser()) {
      const storedLocale = this.storageService.getItem('locale') as UserLocalization;
      if (storedLocale && this.isSupportedLocale(storedLocale)) {
        this.currentLocale.set(storedLocale);
      }
    }

    // Watch for locale changes in storage (cross-tab communication)
    if (this.platformService.isBrowser()) {
      effect(() => {
        const locale = this.currentLocale();
        this.storageService.setItem('locale', locale);
        // Dispatch custom event for cross-tab communication
        window.dispatchEvent(
          new CustomEvent('locale-changed', {
            detail: { locale },
          }),
        );
      });

      // Listen for locale changes from other tabs
      window.addEventListener('storage', (event) => {
        if (event.key === 'locale' && event.newValue) {
          const newLocale = event.newValue as UserLocalization;
          if (this.isSupportedLocale(newLocale)) {
            this.currentLocale.set(newLocale);
          }
        }
      });

      // Listen for custom locale-changed events
      window.addEventListener('locale-changed', ((event: CustomEvent) => {
        if (event.detail?.locale && this.isSupportedLocale(event.detail.locale)) {
          this.currentLocale.set(event.detail.locale);
        }
      }) as EventListener);
    }
  }


  public getSupportedLocales(): UserLocalization[] {
    return [UserLocalization.en_US, UserLocalization.ru_UA, UserLocalization.uk_UA];
  }

  public getCurrentLocale(): UserLocalization {
    return this.currentLocale();
  }

  public setLocale(locale: UserLocalization): void {
    if (this.isSupportedLocale(locale)) {
      this.currentLocale.set(locale);
      // Trigger change detection by dispatching a custom event
      if (this.platformService.isBrowser()) {
        window.dispatchEvent(new Event('locale-changed'));
      }
    }
  }

  /**
   * The translation for `key`, with any `{{name}}` placeholders filled in.
   *
   * `params` is optional and additive: every existing single-argument call
   * behaves exactly as before. It exists because a count or a name belongs
   * *inside* a sentence, and the position it belongs in differs by language —
   * composing "3" + " " + "questions" in code hard-codes English word order and
   * cannot produce "3 питання".
   */
  public instant(key: string | null | undefined, params?: Record<string, string | number>): string {
    if (!key || typeof key !== 'string') {
      return '';
    }
    const locale = this.currentLocale();
    const translation = this.getTranslation(key, locale) || key;
    if (!params) {
      return translation;
    }
    return Object.entries(params).reduce(
      (text, [name, value]) => text.split(`{{${name}}}`).join(String(value)),
      translation
    );
  }

  /**
   * A count and its noun, in the right grammatical form.
   *
   * `key` names an object of `ONE` / `FEW` / `MANY` forms. English and the other
   * Latin locales only distinguish one from many; Ukrainian and Russian need all
   * three, and picking the wrong one is the difference between "3 блоки" and
   * "3 блоків".
   */
  public plural(count: number, key: string): string {
    return `${count} ${this.instant(`${key}.${this.pluralForm(count)}`)}`;
  }

  /** The CLDR form for the active locale, for the three-form Slavic rule. */
  public pluralForm(count: number): 'ONE' | 'FEW' | 'MANY' {
    const locale = this.currentLocale();
    if (locale !== UserLocalization.uk_UA && locale !== UserLocalization.ru_UA) {
      return count === 1 ? 'ONE' : 'MANY';
    }
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11) return 'ONE';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'FEW';
    return 'MANY';
  }

  public get(key: string): string {
    return this.instant(key);
  }

  /**
   * The translation for `key` in a *named* locale, not the active one.
   *
   * `instant()` answers in the language the operator is reading, which is the
   * right default for the interface and the wrong one for authored content: a
   * new module has to be seeded with a title per language, and seeding every
   * language with the reader's own words is how a course ends up "New module"
   * in the Ukrainian column.
   *
   * Falls back to the active locale for a language the catalogue does not
   * carry, so a site publishing a locale this build has no strings for still
   * gets words rather than a raw key.
   */
  public instantFor(
    locale: string,
    key: string,
    params?: Record<string, string | number>
  ): string {
    if (!key) {
      return '';
    }
    const translation = this.isSupportedLocale(locale)
      ? this.getTranslation(key, locale)
      : null;
    const text = translation ?? this.instant(key);
    if (!params) {
      return text;
    }
    return Object.entries(params).reduce(
      (value, [name, param]) => value.split(`{{${name}}}`).join(String(param)),
      text
    );
  }

  private getTranslation(key: string, locale: UserLocalization): string | null {
    const translations = this.translations[locale] || this.translations[UserLocalization.uk_UA];
    const keys = key.split('.');

    let value: any = translations;
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to uk_UA if key not found
        if (locale !== UserLocalization.uk_UA && this.translations[UserLocalization.uk_UA]) {
          const fallbackTranslations = this.translations[UserLocalization.uk_UA];
          let fallbackValue: any = fallbackTranslations;
          for (const fallbackKey of keys) {
            if (fallbackValue && typeof fallbackValue === 'object' && fallbackKey in fallbackValue) {
              fallbackValue = fallbackValue[fallbackKey];
            } else {
              return null;
            }
          }
          return typeof fallbackValue === 'string' ? fallbackValue : null;
        }
        return null;
      }
    }

    return typeof value === 'string' ? value : null;
  }

  private isSupportedLocale(locale: string): locale is UserLocalization {
    return Object.values(UserLocalization).includes(locale as UserLocalization);
  }
}
